import React, { useState, useEffect, useRef, useCallback } from 'react';
import DataTable from 'react-data-table-component';
import { CSVLink } from 'react-csv';
import $ from 'jquery';
import 'bootstrap-datepicker/dist/css/bootstrap-datepicker.min.css';
import 'bootstrap-datepicker/dist/js/bootstrap-datepicker.min.js';
import 'select2/dist/css/select2.min.css';
import 'select2';

const ReferralLogsTable = ({ businessId, token, branches = [] }) => {
    const [loading, setLoading] = useState(false);
    const [logsData, setLogsData] = useState([]);
    const [totalRows, setTotalRows] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [selectedBranch, setSelectedBranch] = useState('all');
    const [sortField, setSortField] = useState('created_at');
    const [sortOrder, setSortOrder] = useState('desc');
    const branchSelectRef = useRef(null);
    const isBranchSelectInitialized = useRef(false);
    const isInitialMount = useRef(true);
    const datepickerInitialized = useRef(false);

    const fetchReferralLogs = useCallback(async () => {
        if (!businessId) return;
        
        setLoading(true);
        try {
            let url = `${process.env.REACT_APP_BASE_URL}/api/referral/logs?business_id=${businessId}&page=${currentPage}&limit=${rowsPerPage}&sortField=${sortField}&sortOrder=${sortOrder}`;
            
            if (searchTerm && searchTerm.trim() !== '') {
                url += `&search=${encodeURIComponent(searchTerm)}`;
            }
            
            if (selectedBranch && selectedBranch !== 'all') {
                url += `&branch_id=${selectedBranch}`;
            }
            
            if (fromDate && fromDate.trim() !== '') {
                url += `&fromDate=${fromDate}`;
            }
            
            if (toDate && toDate.trim() !== '') {
                url += `&toDate=${toDate}`;
            }
            
            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            if (!response.ok) throw new Error('Failed to fetch referral logs');
            
            const data = await response.json();
            setLogsData(data.data || []);
            setTotalRows(data.total || 0);
        } catch (error) {
            console.error('Error fetching referral logs:', error);
        } finally {
            setLoading(false);
        }
    }, [businessId, currentPage, rowsPerPage, sortField, sortOrder, searchTerm, selectedBranch, fromDate, toDate, token]);

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            fetchReferralLogs();
        } else {
            fetchReferralLogs();
        }
    }, [fetchReferralLogs]);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleRowsPerPageChange = (newRowsPerPage, page) => {
        setRowsPerPage(newRowsPerPage);
        setCurrentPage(page);
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        setCurrentPage(1);
    };

    const handleBranchChange = (e) => {
        const branchId = e.target.value;
        setSelectedBranch(branchId);
        setCurrentPage(1);
    };

    const handleApplyFilters = (e) => {
        e.preventDefault();
        setCurrentPage(1);
    };

    const handleClearFilters = () => {
        setFromDate('');
        setToDate('');
        setSelectedBranch('all');
        setSearchTerm('');
        setCurrentPage(1);
        
        if (branchSelectRef.current) {
            $(branchSelectRef.current).val('all').trigger('change');
        }
        $('#fromDate').val('');
        $('#toDate').val('');
    };

    const handleSort = (column, sortDirection) => {
        const fieldMap = {
            "Sender Phone": "sender_phone",
            "Sender Name": "sender_name",
            "Receiver Phone": "receiver_phone",
            "Receiver Name": "receiver_name",
            "Branch Sent From": "branch_sent_from",
            "Promo Code": "promo_code",
            "Status": "is_used",
            "Used At Branch": "used_branch_name",
            "Used Date": "used_at",
            "Created Date": "created_at",
            "Points": "loyalty_points"
        };
        const newSortField = fieldMap[column.name] || 'created_at';
        setSortField(newSortField);
        setSortOrder(sortDirection);
    };

    useEffect(() => {
        if (!datepickerInitialized.current) {
            $('#fromDate').datepicker({
                format: 'yyyy-mm-dd',
                autoclose: true,
                orientation: 'bottom',
                todayHighlight: true
            }).off('changeDate').on('changeDate', (e) => {
                setFromDate(e.target.value);
                setCurrentPage(1);
            });

            $('#toDate').datepicker({
                format: 'yyyy-mm-dd',
                autoclose: true,
                orientation: 'bottom',
                todayHighlight: true
            }).off('changeDate').on('changeDate', (e) => {
                setToDate(e.target.value);
                setCurrentPage(1);
            });
            
            datepickerInitialized.current = true;
        }

        if (branchSelectRef.current && branches && branches.length > 0 && !isBranchSelectInitialized.current) {
            if ($(branchSelectRef.current).data('select2')) {
                $(branchSelectRef.current).select2('destroy');
            }
            
            $(branchSelectRef.current).select2({
                placeholder: 'All Branches',
                allowClear: true,
                width: '100%',
                dropdownParent: $(branchSelectRef.current).parent()
            }).off('change').on('change', handleBranchChange);
            
            isBranchSelectInitialized.current = true;
        }
        
        return () => {
            if (datepickerInitialized.current) {
                $('#fromDate').datepicker('destroy');
                $('#toDate').datepicker('destroy');
                datepickerInitialized.current = false;
            }
            if (branchSelectRef.current && isBranchSelectInitialized.current) {
                try {
                    if ($(branchSelectRef.current).data('select2')) {
                        $(branchSelectRef.current).select2('destroy');
                    }
                } catch (err) {
                    console.warn('Error destroying select2:', err);
                }
                isBranchSelectInitialized.current = false;
            }
        };
    }, [branches]);

    const columns = [
        { name: 'Sender Phone', selector: row => row.sender_phone || '-', sortable: true },
        { name: 'Sender Name', selector: row => row.sender_name || '-', sortable: true },
        { name: 'Receiver Phone', selector: row => row.receiver_phone || '-', sortable: true },
        { name: 'Receiver Name', selector: row => row.receiver_name || '-', sortable: true },
        { name: 'Branch Sent From', selector: row => row.branch_sent_from || '-', sortable: true },
        { name: 'Promo Code', selector: row => row.promo_code || '-', sortable: true },
        { 
            name: 'Status', 
            selector: row => row.is_used,
            sortable: true,
            cell: row => (
                row.is_used === 1 ? 
                    <span className="badge badge-success">Used</span> : 
                    <span className="badge badge-warning">Pending</span>
            )
        },
        { name: 'Used At Branch', selector: row => row.used_branch_name || '-', sortable: true },
        { 
            name: 'Used Date', 
            selector: row => row.used_at,
            sortable: true,
            cell: row => row.used_at ? new Date(row.used_at).toLocaleString() : '-'
        },
        { 
            name: 'Created Date', 
            selector: row => row.created_at,
            sortable: true,
            cell: row => new Date(row.created_at).toLocaleString()
        },
        { name: 'Points', selector: row => row.loyalty_points || 0, sortable: true }
    ];

    const csvData = logsData.map(row => ({
        'Sender Phone': row.sender_phone || '-',
        'Sender Name': row.sender_name || '-',
        'Receiver Phone': row.receiver_phone || '-',
        'Receiver Name': row.receiver_name || '-',
        'Branch Sent From': row.branch_sent_from || '-',
        'Promo Code': row.promo_code || '-',
        'Status': row.is_used === 1 ? 'Used' : 'Pending',
        'Used At Branch': row.used_branch_name || '-',
        'Used Date': row.used_at ? new Date(row.used_at).toLocaleString() : '-',
        'Created Date': new Date(row.created_at).toLocaleString(),
        'Points': row.loyalty_points || 0
    }));

    const handlePrint = () => {
        window.print();
    };

    return (
        <div className="content">
            <div className="col-sm-12 print-hidden">
                <div className="card-box">
                    <h4 className="header-title m-t-0 m-b-30">Filters:</h4>
                    <form id='filter-form' onSubmit={handleApplyFilters}>
                        <div className="row">
                            <div className="col-lg-3 col-md-6 col-sm-12">
                                <label className="control-label">From Date:</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="fromDate"
                                    placeholder="YYYY-MM-DD"
                                    autoComplete="off"
                                />
                            </div>
                            <div className="col-lg-3 col-md-6 col-sm-12">
                                <label className="control-label">To Date:</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="toDate"
                                    placeholder="YYYY-MM-DD"
                                    autoComplete="off"
                                />
                            </div>
                            <div className="col-lg-3 col-md-6 col-sm-12">
                                <label className="control-label">Branch:</label>
                                <select
                                    ref={branchSelectRef}
                                    className="form-control"
                                    defaultValue="all"
                                >
                                    <option value="all">All Branches</option>
                                    {branches && branches.map((branch) => (
                                        <option key={branch.id_branch} value={branch.id_branch}>
                                            {branch.branch_name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-lg-3 col-md-6 col-sm-12">
                                <label className="control-label" style={{ visibility: 'hidden' }}>Not Visible</label>
                                <button type="submit" className="btn btn-primary" id="runButton">Run</button>
                                <div className="filters-buttons-div" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                    <a href="#" id="clear-btn" onClick={(e) => { e.preventDefault(); handleClearFilters(); }}>Clear</a>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
            
            <div className='col-12' style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', marginTop: '30px' }}>
                <div>
                    <CSVLink
                        data={csvData}
                        filename={`referral_logs_${new Date().toISOString().split('T')[0]}.csv`}
                        className="btn btn-primary print-hidden"
                        style={{ margin: '2px' }}
                    >
                        CSV
                    </CSVLink>
                    <button className="btn btn-primary print-hidden" onClick={handlePrint} style={{ margin: '2px' }}>
                        Print
                    </button>
                </div>
                <input
                    type="text"
                    className='form-control print-hidden'
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    style={{ width: '200px' }}
                />
            </div>
            
            <div className='col-12'>
                <DataTable
                    columns={columns}
                    data={logsData}
                    progressPending={loading}
                    pagination
                    paginationServer
                    paginationTotalRows={totalRows}
                    onChangePage={handlePageChange}
                    onChangeRowsPerPage={handleRowsPerPageChange}
                    paginationRowsPerPageOptions={[10, 15, 20, 25, 30, 50, 100]}
                    onSort={handleSort}
                    sortServer
                    defaultSortFieldId="created_at"
                    defaultSortAsc={false}
                    striped
                    responsive
                    noDataComponent="No referral logs found."
                />
            </div>
        </div>
    );
};

export default ReferralLogsTable;